import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { LinkButton } from 'components/common';
import { CoursesTileCopy } from '.';

import type { ButtonCategory, ButtonSize } from 'components/common/Button';
import type { CourseInView } from './Courses';

interface Props {
  course: CourseInView;
}

const CoursesAdminActions = ({ course }: Props) => {
  const { messages, user } = useReduxState();

  const {
    adminAccess,
    adminPermissions,
    chapters,
    id,
    isOwnerOrSuperadmin,
    slug,
    title,
    unreadFeedbackCount,
    unreviewedAnswerCount,
    unseenAccessRequests
  } = course;

  const adminLinkProps = {
    category: 'lighter' as ButtonCategory,
    isSemiRounded: true,
    onClick: (ev) => ev.stopPropagation(),
    size: 'small' as ButtonSize,
    style: {
      flexShrink: 0,
      fontSize: '1.3rem',
      width: 30,
      height: 30
    }
  };

  return (
    <div className="actions">
      {isOwnerOrSuperadmin || adminPermissions.includes('edit-content') ? (
        <div className="admin-link">
          <LinkButton
            href={`/admin/courses/${slug}/edit`}
            icon="pen"
            tooltip={messages.pagesEdit}
            unreadItems={
              chapters.filter((chapter) => !chapter.publishDate).length
            }
            warningItems={chapters.filter((chapter) => chapter.isDraft).length}
            {...adminLinkProps}
          />
        </div>
      ) : null}

      {isOwnerOrSuperadmin || adminPermissions.includes('edit-settings') ? (
        <div className="admin-link">
          <LinkButton
            href={`/admin/courses/${slug}/settings`}
            icon="cog"
            tooltip={messages.pagesSettings}
            {...adminLinkProps}
          />
        </div>
      ) : null}

      {isOwnerOrSuperadmin ? (
        <div className="admin-link">
          <LinkButton
            href={`/admin/courses/${slug}/publish`}
            icon="check-circle"
            tooltip={messages.commonPublish}
            {...adminLinkProps}
          />
        </div>
      ) : null}

      {isOwnerOrSuperadmin || adminPermissions.includes('manage-users') ? (
        <div className="admin-link">
          <LinkButton
            href={`/admin/courses/${slug}/share`}
            icon="user"
            tooltip={messages.commonShare}
            unreadItems={unseenAccessRequests}
            {...adminLinkProps}
          />
        </div>
      ) : null}

      {isOwnerOrSuperadmin ||
      (adminPermissions.includes('review-tasks') &&
        adminPermissions.includes('view-feedback')) ? (
        <div className="admin-link">
          <LinkButton
            href={`/admin/courses/${slug}/follow/users`}
            icon="chart-line"
            tooltip={messages.pagesFollow}
            unreadItems={unreviewedAnswerCount + unreadFeedbackCount}
            {...adminLinkProps}
          />
        </div>
      ) : adminPermissions.includes('review-tasks') ? (
        <div className="admin-link">
          <LinkButton
            href={`/admin/courses/${slug}/follow/users`}
            icon="chart-line"
            tooltip={messages.pagesFollow}
            unreadItems={unreviewedAnswerCount}
            {...adminLinkProps}
          />
        </div>
      ) : adminPermissions.includes('view-feedback') ? (
        <div className="admin-link">
          <LinkButton
            href={`/admin/courses/${slug}/follow/feedback`}
            icon="chart-line"
            tooltip={messages.pagesFollow}
            unreadItems={unreadFeedbackCount}
            {...adminLinkProps}
          />
        </div>
      ) : adminPermissions.includes('view-analytics') ? (
        <div className="admin-link">
          <LinkButton
            href={`/admin/courses/${slug}/follow/visitors`}
            icon="chart-line"
            tooltip={messages.pagesFollow}
            {...adminLinkProps}
          />
        </div>
      ) : null}

      {isOwnerOrSuperadmin ? (
        <div className="admin-link">
          <LinkButton
            href={`/admin/courses/${slug}/manage`}
            icon="tools"
            tooltip={messages.pagesManage}
            {...adminLinkProps}
          />
        </div>
      ) : null}

      {adminAccess === 'copyable' &&
      !isOwnerOrSuperadmin &&
      user &&
      user.roles.includes('admin') &&
      user.permissions.includes('create-courses') ? (
        <CoursesTileCopy courseId={id} courseTitle={title} />
      ) : null}

      <style jsx>{`
        .actions {
          display: flex;
          flex-grow: 0;
          flex-shrink: 0;
          flex-wrap: wrap;
          gap: 8px;
        }
        .dropdown {
          flex-grow: 0;
          flex-shrink: 0;
          margin-right: 10px;
        }
        @media (min-width: ${breakpoints.large}px) {
          .admin-link {
            display: inline-block;
          }
          .dropdown {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default CoursesAdminActions;
